<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import type { AuthSubmitMode } from '@/entities/session/auth-errors';
import { t } from '@/shared/i18n/messages';
import { trackRegisterSubmit } from '@/shared/tracking/meta-pixel';
import {
  inviteFieldLocked,
  inviteFieldValue,
  referralCodeForRegister,
} from '@/shared/referral/invite-link';
import { useSessionStore } from '@/stores/session-store';

/** 本地皮肤资源（避免热载 gc987.com 失败） */
const GC987_ASSETS = {
  close: '/skins/gc987-h5/auth/close.svg',
  flag: '/skins/gc987-h5/auth/br-flag.svg',
  logo: '/skins/gc987-h5/auth/logo.png',
  password: '/skins/gc987-h5/auth/password.svg',
} as const;

const REMEMBER_KEY = 'fc.auth.rememberPhone';

const props = withDefaults(
  defineProps<{
    agentCode?: string;
    mode?: AuthSubmitMode;
    referralCode?: string;
  }>(),
  { agentCode: '', mode: 'login', referralCode: '' },
);

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const sessionStore = useSessionStore();
const inviteLocked = computed(() => inviteFieldLocked(props.referralCode, props.agentCode));
const mode = ref<AuthSubmitMode>(inviteLocked.value ? 'register' : props.mode);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const submitLock = ref(false);
const rememberPassword = ref(true);
const adultConfirmed = ref(true);
const localError = ref('');

const remembered = (() => {
  try {
    return localStorage.getItem(REMEMBER_KEY) || '';
  } catch {
    return '';
  }
})();

const form = reactive({
  confirmPassword: '',
  loginName: remembered,
  password: '',
  referralCode: inviteFieldValue(props.referralCode, props.agentCode),
});

watch(
  () => [props.referralCode, props.agentCode] as const,
  () => {
    const next = inviteFieldValue(props.referralCode, props.agentCode);
    if (next) {
      form.referralCode = next;
      mode.value = 'register';
    }
  },
);

watch(
  () => props.mode,
  (value) => {
    if (!inviteLocked.value) mode.value = value;
  },
);

const isRegister = computed(() => mode.value === 'register');
const visibleError = computed(() => localError.value || sessionStore.lastError);

const phoneDigits = computed(() => form.loginName.replace(/\D/g, ''));
const passwordOk = computed(() => form.password.length >= 6 && form.password.length <= 16);
const confirmOk = computed(() => !isRegister.value || form.confirmPassword === form.password);
const referralDigits = computed(() => form.referralCode.trim());
const canSubmit = computed(
  () =>
    phoneDigits.value.length === 11 &&
    passwordOk.value &&
    confirmOk.value &&
    (!isRegister.value || adultConfirmed.value) &&
    !submitLock.value &&
    !sessionStore.submitting,
);

const submitLabel = computed(() => {
  if (sessionStore.submitting) {
    return isRegister.value ? t('auth.register.submitting') : t('auth.login.submitting');
  }
  return isRegister.value ? t('auth.register.submit') : t('auth.login.submit');
});

const heading = computed(() =>
  isRegister.value ? t('auth.register.heading') : t('auth.login.heading'),
);

const switchHint = computed(() =>
  isRegister.value ? t('auth.action.hasAccount') : t('auth.action.noAccount'),
);

const switchLabel = computed(() =>
  isRegister.value ? t('auth.tab.login') : t('auth.tab.register'),
);

function setMode(nextMode: AuthSubmitMode) {
  mode.value = nextMode;
  localError.value = '';
  sessionStore.clearError();
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  form.loginName = target.value.replace(/\D/g, '').slice(0, 11);
}

function clearPhone() {
  form.loginName = '';
}

function clearPassword() {
  form.password = '';
}

function clearConfirmPassword() {
  form.confirmPassword = '';
}

function clearReferral() {
  if (inviteLocked.value) return;
  form.referralCode = '';
}

function validateForm() {
  if (phoneDigits.value.length !== 11) {
    localError.value = t('auth.error.loginName');
    return false;
  }

  if (!passwordOk.value) {
    localError.value = t('auth.error.password');
    return false;
  }

  if (isRegister.value && form.confirmPassword !== form.password) {
    localError.value = t('auth.error.passwordMismatch');
    return false;
  }

  if (isRegister.value && !adultConfirmed.value) {
    localError.value = t('auth.error.age');
    return false;
  }

  localError.value = '';
  return true;
}

const submit = async () => {
  if (submitLock.value || sessionStore.submitting) return;
  submitLock.value = true;
  try {
    if (!validateForm()) return;

    try {
      if (rememberPassword.value) {
        localStorage.setItem(REMEMBER_KEY, phoneDigits.value);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      /* ignore storage failures */
    }

    if (isRegister.value) trackRegisterSubmit();
    const success = isRegister.value
      ? await sessionStore.register({
          loginName: phoneDigits.value,
          password: form.password,
          phone: phoneDigits.value,
          referralCode:
            referralCodeForRegister(form.referralCode, props.referralCode, props.agentCode) ||
            undefined,
          agentCode: props.agentCode.trim() || undefined,
        })
      : await sessionStore.login({
          loginName: phoneDigits.value,
          password: form.password,
        });

    if (success) emit('success');
  } finally {
    submitLock.value = false;
  }
};
</script>

<template>
  <div class="gc-auth">
    <div class="gc-auth__chrome">
      <div class="gc-auth__chrome-bar">
        <img class="gc-auth__logo" :src="GC987_ASSETS.logo" alt="GC987.com" width="120" height="28" />
        <button
          class="gc-auth__close"
          type="button"
          :aria-label="t('auth.action.close')"
          @click="emit('close')"
        >
          <img :src="GC987_ASSETS.close" alt="" width="12" height="13" />
        </button>
      </div>
    </div>

    <form class="gc-auth__panel" autocomplete="off" @submit.prevent="submit">
      <header
        class="gc-auth__header"
        :class="isRegister ? 'gc-auth__header--register' : 'gc-auth__header--login'"
      >
        <h1 class="gc-auth__title">{{ heading }}</h1>
        <p class="gc-auth__switch">
          <span>{{ switchHint }}</span>
          <button type="button" @click="setMode(isRegister ? 'login' : 'register')">
            {{ switchLabel }}
          </button>
        </p>
      </header>

      <!-- 源站 form-wrapper：flex:auto + padding-top:.5rem -->
      <div class="gc-auth__body">
      <div class="gc-auth__fields">
        <label class="gc-auth__field" :class="{ 'gc-auth__field--filled': phoneDigits.length > 0 }">
          <span class="gc-auth__prefix">
            <span
              class="gc-auth__flag"
              :style="{ backgroundImage: `url(${GC987_ASSETS.flag})` }"
              title="BR"
              aria-hidden="true"
            />
            <span class="gc-auth__cc">+55</span>
          </span>
          <input
            :value="form.loginName"
            autocomplete="username"
            inputmode="numeric"
            maxlength="11"
            minlength="11"
            name="username"
            required
            type="tel"
            :placeholder="t('auth.field.loginName')"
            @input="onPhoneInput"
          />
          <button
            v-if="form.loginName"
            class="gc-auth__clear"
            type="button"
            :aria-label="t('auth.action.clear')"
            @click="clearPhone"
          >
            ×
          </button>
        </label>

        <label class="gc-auth__field" :class="{ 'gc-auth__field--filled': form.password.length > 0 }">
          <img class="gc-auth__icon" :src="GC987_ASSETS.password" alt="" width="20" height="20" />
          <input
            v-model="form.password"
            :autocomplete="isRegister ? 'new-password' : 'current-password'"
            maxlength="16"
            minlength="6"
            required
            :type="showPassword ? 'text' : 'password'"
            :placeholder="t('auth.field.password')"
          />
          <button
            v-if="form.password"
            class="gc-auth__clear"
            type="button"
            :aria-label="t('auth.action.clear')"
            @click="clearPassword"
          >
            ×
          </button>
          <button
            class="gc-auth__eye"
            type="button"
            :aria-label="showPassword ? t('auth.action.hide') : t('auth.action.show')"
            @click="showPassword = !showPassword"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                v-if="!showPassword"
                fill="currentColor"
                d="M12 5c-5.5 0-9.5 4.5-10.8 6.2a1.2 1.2 0 0 0 0 1.6C2.5 14.5 6.5 19 12 19s9.5-4.5 10.8-6.2a1.2 1.2 0 0 0 0-1.6C21.5 9.5 17.5 5 12 5Zm0 11.2A4.2 4.2 0 1 1 12 7.8a4.2 4.2 0 0 1 0 8.4Zm0-2.4a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z"
              />
              <path
                v-else
                fill="currentColor"
                d="M3.3 2.5 2 3.8l3.1 3.1C3.4 8.2 1.9 9.9 1.2 11a1.2 1.2 0 0 0 0 1.5C2.5 14.2 6.5 19 12 19c2 0 3.8-.6 5.3-1.5l3.4 3.4 1.3-1.3L3.3 2.5ZM12 17.2c-4.5 0-7.9-3.5-9.2-5.2.7-1 2-2.5 3.7-3.6l2.1 2.1A4.2 4.2 0 0 0 12 15.4c.5 0 1-.1 1.4-.3l1.6 1.6c-.9.3-1.9.5-3 .5Zm8.9-4.5c-.4.5-1 1.2-1.8 2l-2.4-2.4a4.2 4.2 0 0 0-5-5L9.3 4.9C10.1 4.6 11 4.5 12 4.5c5.5 0 9.5 4.8 10.8 6.5a1.2 1.2 0 0 1 0 1.5c-.3.4-.6.8-1 .1.2Z"
              />
            </svg>
          </button>
        </label>

        <label
          v-if="isRegister"
          class="gc-auth__field"
          :class="{ 'gc-auth__field--filled': form.confirmPassword.length > 0 }"
        >
          <img class="gc-auth__icon" :src="GC987_ASSETS.password" alt="" width="20" height="20" />
          <input
            v-model="form.confirmPassword"
            autocomplete="new-password"
            maxlength="16"
            minlength="6"
            required
            :type="showConfirmPassword ? 'text' : 'password'"
            :placeholder="t('auth.field.confirmPassword')"
          />
          <button
            v-if="form.confirmPassword"
            class="gc-auth__clear"
            type="button"
            :aria-label="t('auth.action.clear')"
            @click="clearConfirmPassword"
          >
            ×
          </button>
          <button
            class="gc-auth__eye"
            type="button"
            :aria-label="showConfirmPassword ? t('auth.action.hide') : t('auth.action.show')"
            @click="showConfirmPassword = !showConfirmPassword"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 5c-5.5 0-9.5 4.5-10.8 6.2a1.2 1.2 0 0 0 0 1.6C2.5 14.5 6.5 19 12 19s9.5-4.5 10.8-6.2a1.2 1.2 0 0 0 0-1.6C21.5 9.5 17.5 5 12 5Zm0 11.2A4.2 4.2 0 1 1 12 7.8a4.2 4.2 0 0 1 0 8.4Zm0-2.4a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z"
              />
            </svg>
          </button>
        </label>

        <label
          v-if="isRegister"
          class="gc-auth__field"
          :class="{
            'gc-auth__field--filled': referralDigits.length > 0,
            'gc-auth__field--locked': inviteLocked,
          }"
        >
          <svg class="gc-auth__icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              fill="currentColor"
              d="M20 7h-2.2A3 3 0 0 0 12 3.2 3 3 0 0 0 6.2 7H4a1 1 0 0 0-1 1v3h18V8a1 1 0 0 0-1-1ZM9 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm6 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM3 13v7a1 1 0 0 0 1 1h7v-8H3Zm10 8h7a1 1 0 0 0 1-1v-7h-8v8Z"
            />
          </svg>
          <input
            v-model="form.referralCode"
            autocomplete="off"
            maxlength="32"
            name="referralCode"
            type="text"
            :readonly="inviteLocked"
            :aria-readonly="inviteLocked"
            :placeholder="t('auth.field.referralCode')"
          />
          <button
            v-if="form.referralCode && !inviteLocked"
            class="gc-auth__clear"
            type="button"
            :aria-label="t('auth.action.clear')"
            @click="clearReferral"
          >
            ×
          </button>
        </label>
      </div>

      <div v-if="!isRegister" class="gc-auth__options">
        <label class="gc-auth__remember">
          <input v-model="rememberPassword" type="checkbox" />
          <span class="gc-auth__check" aria-hidden="true" />
          <span>{{ t('auth.option.remember') }}</span>
        </label>
        <span class="gc-auth__forgot">{{ t('auth.action.recover') }}</span>
      </div>

      <div v-else class="gc-auth__options gc-auth__options--adult">
        <label class="gc-auth__remember">
          <input v-model="adultConfirmed" type="checkbox" />
          <span class="gc-auth__check" aria-hidden="true" />
          <span>{{ t('auth.option.age') }}</span>
        </label>
      </div>

      <p v-if="visibleError" class="gc-auth__error">{{ visibleError }}</p>
      </div>

      <!-- 源站 footer：margin-top:auto; margin-bottom:1.38rem -->
      <div class="gc-auth__footer">
        <div
          class="gc-auth__submit-wrap"
          :class="{ 'gc-auth__submit-wrap--login': !isRegister }"
        >
          <span v-if="isRegister" class="gc-auth__reward" aria-hidden="true">{{
            t('auth.register.rewardTag')
          }}</span>
          <button
            class="gc-auth__submit"
            type="submit"
            :disabled="!canSubmit"
            :aria-busy="submitLock || sessionStore.submitting"
          >
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
